

import React, { useEffect, useLayoutEffect } from 'react';
import { X } from 'lucide-react';
import { Z_INDEX } from '../constants/zIndex';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Override stacking (e.g. session-expired above normal dialogs, still below toast). */
  overlayZIndex?: number;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, title, size = 'sm', overlayZIndex }) => {
  // Close on escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevent background scrolling
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // Return focus to the control that opened the dialog (avoids scroll jump / focus loss)
  useLayoutEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    return () => {
      if (previous && typeof previous.focus === 'function' && document.body.contains(previous)) {
        previous.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl'
  };

  return (
    <div
      className="fixed inset-0 flex min-h-0 items-center justify-center px-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-4 sm:py-4"
      style={{ zIndex: overlayZIndex ?? Z_INDEX.modal }}
    >
      <div
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-sm transition-opacity duration-200 ease-in-out animate-in fade-in md:backdrop-blur-[10px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`relative flex w-full min-h-0 max-h-[min(92dvh,100dvh-1.5rem)] flex-col overflow-hidden rounded-[1.75rem] bg-white p-5 shadow-[0_30px_70px_-10px_rgba(15,23,42,0.2)] sm:max-h-[90vh] sm:rounded-[2.5rem] sm:p-8 md:p-10 ${sizeClasses[size]} transform transition-all duration-200 ease-in-out animate-in fade-in zoom-in-95 ease-out`}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-400 touch-manipulation hover:bg-slate-50 hover:text-slate-900 sm:right-6 sm:top-6"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
        {title && (
          <h3 className="mb-5 shrink-0 pr-11 text-xl font-bold tracking-tight text-slate-900 sm:mb-8 sm:text-2xl md:text-3xl">
            {title}
          </h3>
        )}
        <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain -mx-1 px-1 sm:-mx-2 sm:px-2 custom-scrollbar">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
